import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, AlertTriangle } from "lucide-react";
import { api, formatTL } from "../lib/api";
import { Progress } from "./ui/progress";

export default function BudgetStatus({ month, refreshKey }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get(`/budget/status?month=${month}`).then((r) => setStatus(r.data)).catch(() => {});
  }, [month, refreshKey]);

  if (!status) return null;
  const hasGoal = status.savings_goal > 0;
  const hasLimits = status.limits.length > 0;

  if (!hasGoal && !hasLimits) {
    return (
      <div className="bg-card border border-dashed border-border rounded-2xl p-6 text-center" data-testid="budget-empty-hint">
        <p className="text-sm text-muted-foreground">
          Aylık tasarruf hedefi ve kategori bütçe limitleri belirlemek için{" "}
          <Link to="/ayarlar" className="text-primary hover:underline">Ayarlar</Link> sayfasına gidin.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="budget-status">
      {hasGoal && (
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="savings-goal-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">Aylık Tasarruf Hedefi</h2>
              <p className="text-xs text-muted-foreground">Hedef: {formatTL(status.savings_goal)}</p>
            </div>
          </div>
          <Progress value={status.goal_pct} className="h-3 bg-secondary" data-testid="savings-goal-progress" />
          <div className="flex justify-between mt-2 text-sm">
            <span data-testid="savings-goal-saved" className={status.saved >= 0 ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>
              {formatTL(status.saved)} biriktirildi
            </span>
            <span className="text-muted-foreground">%{status.goal_pct}</span>
          </div>
          {status.saved >= status.savings_goal && (
            <p className="text-xs text-emerald-400 mt-2">Tebrikler! Bu ayki hedefinize ulaştınız 🎉</p>
          )}
        </div>
      )}

      {hasLimits && (
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="budget-limits-card">
          <h2 className="font-heading text-lg font-semibold mb-4">Bütçe Limitleri</h2>
          <div className="space-y-4">
            {status.limits.map((l) => (
              <div key={l.category} data-testid={`budget-limit-${l.category}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{l.category}</span>
                    {l.exceeded && (
                      <span data-testid={`budget-exceeded-${l.category}`}
                        className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/15 rounded-full px-2 py-0.5">
                        <AlertTriangle className="w-3 h-3" /> Limit aşıldı!
                      </span>
                    )}
                  </div>
                  <span className={`text-xs ${l.exceeded ? "text-red-400 font-semibold" : "text-muted-foreground"}`}>
                    {formatTL(l.spent)} / {formatTL(l.limit)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full rounded-full transition-[width] duration-500 ${l.exceeded ? "bg-red-500" : l.pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${l.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
